import { Application, Assets, Graphics } from 'pixi.js';
import { Spine } from '@esotericsoftware/spine-pixi-v8';
import type { World, ComponentStore } from '@ecs/world';
import { DEFAULT_TUNING, type Tuning } from '@game/tuning';
import { SpineAnimationController } from '@game/SpineAnimationController';
import { SPINEBOY_PRO_ATLAS_ASSET_ID, SPINEBOY_PRO_SKELETON_ASSET_ID } from '@game/spineAssets';
import type {
	AmmoComponent,
	KinematicComponent,
	PlatformComponent,
	PlatformResource,
	PlayerState,
	SpineComponent,
	StageBounds,
	TransformComponent,
	VelocityComponent
} from '@game/scenes/characterPrototype/types';

export type CreatePlayerOptions = {
	boundsKey: string;
	platformKey: string;
	playerColliderHeightScale: number;
	playerVisualYOffset: number;
	ammoClipSize: number;
};

export type SpawnPlatformFn = (
	world: World,
	platforms: ComponentStore<PlatformComponent>,
	graphics: ComponentStore<Graphics>,
	stage: Application['stage'],
	x: number,
	y: number,
	width: number,
	height: number
) => number;

export function createPlayer(
	world: World,
	transforms: ComponentStore<TransformComponent>,
	velocities: ComponentStore<VelocityComponent>,
	kinematics: ComponentStore<KinematicComponent>,
	states: ComponentStore<PlayerState>,
	graphics: ComponentStore<Graphics>,
	debugColliderGraphics: ComponentStore<Graphics>,
	ammos: ComponentStore<AmmoComponent>,
	ammoGraphics: ComponentStore<Graphics>,
	dashGraphics: ComponentStore<Graphics>,
	stage: Application['stage'],
	platforms: ComponentStore<PlatformComponent>,
	spines: ComponentStore<SpineComponent>,
	spawnPlatform: SpawnPlatformFn,
	options: CreatePlayerOptions
) {
	const tuning = world.getResource<Tuning>('tuning') ?? DEFAULT_TUNING;
	const entity = world.createEntity();
	const bounds = world.getResource<StageBounds>(options.boundsKey);
	const platform = world.getResource<PlatformResource>(options.platformKey);
	const startX = bounds ? bounds.width / 2 : 0;
	const startY = platform ? platform.y - tuning.playerSize : bounds ? bounds.height / 2 : 0;

	transforms.set(entity, { x: startX, y: startY });
	velocities.set(entity, { x: 0, y: 0 });
	kinematics.set(entity, {
		speed: tuning.playerSpeed,
		halfSize: tuning.playerSize / 2,
		halfHeight: (tuning.playerSize / 2) * options.playerColliderHeightScale
	});
	states.set(entity, {
		grounded: Boolean(platform),
		canJump: Boolean(platform),
		jumpCharge: 0,
		climbingLedge: false,
		ledgeTargetY: 0,
		skipJumpLock: 0,
		dropThroughTimer: 0,
		dropHoldTimer: 0,
		groundedPlatform: null,
		wallRunning: false,
		wallSide: 1,
		wallRunTime: 0,
		wallRunLock: 0,
		wallRunStartY: 0,
		wallRunExhausted: false,
		meleeCooldown: 0,
		dashCooldown: 0,
		dashTime: 0,
		dashDirX: 0,
		dashDirY: 0
	});

	// Create Spine character instead of cube
	let spineInstance: Spine | null = null;
	try {
		// Check if there was an error loading
		if (Assets.cache.has('spineboy-pro-error')) {
			console.warn('Spine loading failed earlier, using fallback cube');
		} else {
			// spine-pixi-v8 expects skeleton+atlas already loaded into Pixi Assets
			const scale = tuning.playerSize / 262;
			spineInstance = Spine.from({
				skeleton: SPINEBOY_PRO_SKELETON_ASSET_ID,
				atlas: SPINEBOY_PRO_ATLAS_ASSET_ID,
				scale,
				autoUpdate: false
			});

			spineInstance.position.set(startX, startY);
			spineInstance.position.y += options.playerVisualYOffset;
			stage.addChild(spineInstance);

			const controller = new SpineAnimationController(spineInstance);
			spines.set(entity, { spine: spineInstance, controller, lastDebugAnimToken: -1 });
		}
	} catch (error) {
		console.warn('Failed to create Spine character, falling back to cube:', error);
	}

	// Fallback to cube if Spine failed
	if (!spineInstance) {
		const cube = new Graphics();
		cube.roundRect(-tuning.playerSize / 2, -tuning.playerSize / 2, tuning.playerSize, tuning.playerSize, 8);
		cube.fill({ color: 0x38bdf8, alpha: 0.85 });
		cube.stroke({ color: 0xffffff, alpha: 0.35, width: 2 });
		cube.position.set(startX, startY);
		stage.addChild(cube);
		graphics.set(entity, cube);
	}

	// Debug: visualize player collider (physics stays at transform, sprite can be offset)
	const dbg = new Graphics();
	dbg.position.set(startX, startY);
	stage.addChild(dbg);
	debugColliderGraphics.set(entity, dbg);

	ammos.set(entity, {
		capacity: options.ammoClipSize,
		remaining: options.ammoClipSize,
		reloadDuration: Math.max(0.05, tuning.playerReloadDuration),
		reloadRemaining: 0,
		isEnemy: false
	});
	const ammoBar = new Graphics();
	ammoBar.position.set(startX, startY);
	stage.addChild(ammoBar);
	ammoGraphics.set(entity, ammoBar);

	const dashBar = new Graphics();
	dashBar.position.set(startX, startY);
	stage.addChild(dashBar);
	dashGraphics.set(entity, dashBar);

	if (platform) {
		const baseId = spawnPlatform(world, platforms, graphics, stage, platform.x, platform.y, platform.width, platform.height);
		world.setResource<number | null>('base-platform-entity', baseId);
	}

	return entity;
}


